import { diffDays, todayIn, type DateOnly } from '@/domain/dates'

/**
 * Formatting always uses the business timezone (Europe/Lisbon) and pt-PT,
 * never the browser's locale/timezone.
 */
export const BUSINESS_TZ = 'Europe/Lisbon'

const longDateFmt = new Intl.DateTimeFormat('pt-PT', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
const numericFmt = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
const timeFmt = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit', timeZone: BUSINESS_TZ })
const dateTimeFmt = new Intl.DateTimeFormat('pt-PT', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: BUSINESS_TZ,
})

function clean(s: string) {
  return s.replace(/\./g, '').replace(/ de /g, ' ')
}

export function today(): DateOnly {
  return todayIn(BUSINESS_TZ)
}

function dateOnlyToDate(d: DateOnly) {
  return new Date(`${d}T12:00:00Z`)
}

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

/** "18 out" (current year) or "18 out 2027". Built by hand: Intl output varies between browsers. */
export function formatDate(d: DateOnly | null | undefined): string {
  if (!d) return '—'
  const label = `${Number(d.slice(8, 10))} ${MONTHS[Number(d.slice(5, 7)) - 1]}`
  return d.slice(0, 4) === today().slice(0, 4) ? label : `${label} ${d.slice(0, 4)}`
}

export function formatDateNumeric(d: DateOnly | null | undefined): string {
  if (!d) return '—'
  return numericFmt.format(dateOnlyToDate(d))
}

export function formatLongDate(d: DateOnly): string {
  const s = longDateFmt.format(dateOnlyToDate(d))
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Relative wording for deadlines: "Hoje", "Amanhã", "Em 5 dias", "Há 2 dias". */
export function relativeDay(d: DateOnly, base: DateOnly = today()): string {
  const n = diffDays(base, d)
  if (n === 0) return 'Hoje'
  if (n === 1) return 'Amanhã'
  if (n === -1) return 'Ontem'
  if (n > 1 && n <= 30) return `Em ${n} dias`
  if (n < -1 && n >= -30) return `Há ${-n} dias`
  return formatDate(d)
}

export function daysUntil(d: DateOnly, base: DateOnly = today()): number {
  return diffDays(base, d)
}

export function formatTime(ts: string): string {
  return timeFmt.format(new Date(ts))
}

export function formatDateTime(ts: string | null | undefined): string {
  if (!ts) return '—'
  return clean(dateTimeFmt.format(new Date(ts)))
}

/** Timestamp → business date in Lisbon. */
export function tsToDateOnly(ts: string): DateOnly {
  return todayIn(BUSINESS_TZ, new Date(ts))
}

/** "agora", "há 5 min", "há 3 h", "ontem 14:32", "06 out 14:32". */
export function formatRelativeTime(ts: string, now = new Date()): string {
  const t = new Date(ts)
  const diff = (now.getTime() - t.getTime()) / 1000
  if (diff < 45) return 'agora'
  if (diff < 3600) return `há ${Math.round(diff / 60)} min`
  if (diff < 6 * 3600) return `há ${Math.round(diff / 3600)} h`
  const day = tsToDateOnly(ts)
  const n = diffDays(day, todayIn(BUSINESS_TZ, now))
  if (n === 0) return `hoje ${formatTime(ts)}`
  if (n === 1) return `ontem ${formatTime(ts)}`
  return formatDateTime(ts)
}

export function greeting(now = new Date()): string {
  const hour = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: BUSINESS_TZ }).format(now))
  if (hour < 6) return 'Boa noite'
  if (hour < 13) return 'Bom dia'
  if (hour < 20) return 'Boa tarde'
  return 'Boa noite'
}

export function firstName(fullName: string | null | undefined): string {
  return (fullName ?? '').trim().split(/\s+/)[0] ?? ''
}

/** "12–16 out" or "28 dez – 3 jan 2027": compact period label. */
export function formatPeriod(start: DateOnly, end: DateOnly): string {
  if (start === end) return formatDate(start)
  if (start.slice(0, 7) === end.slice(0, 7)) return `${Number(start.slice(8, 10))}–${formatDate(end)}`
  if (start.slice(0, 4) === end.slice(0, 4)) return `${formatDate(start).replace(/ \d{4}$/, '')} – ${formatDate(end)}`
  return `${formatDate(start)} – ${formatDate(end)}`
}

export function formatMonthYear(year: number, month: number): string {
  const s = new Intl.DateTimeFormat('pt-PT', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, 15)))
  return s.charAt(0).toUpperCase() + s.slice(1).replace(' de ', ' ')
}

export function days(n: number): string {
  return `${n} ${Math.abs(n) === 1 ? 'dia' : 'dias'}`
}
